
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Store, Users, Building, Info, Crown, Shield, Calendar, Home } from "lucide-react";
import { PlatformInfo, PlatformStrategy } from "@/utils/platform-intelligence";
import { useSubscriptionFeatures } from "@/hooks/use-subscription-features";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface PlatformIntelligenceCardProps {
  platformInfo: PlatformInfo;
  strategy: PlatformStrategy;
  recommendations: string[];
}

const PlatformIntelligenceCard = ({ 
  platformInfo, 
  strategy, 
  recommendations 
}: PlatformIntelligenceCardProps) => {
  const { canAccessPlatformIntelligence } = useSubscriptionFeatures();
  const navigate = useNavigate();

  const getPlatformIcon = () => {
    switch (platformInfo.platform) {
      case 'amazon':
      case 'ebay':
      case 'etsy':
        return <Store className="h-4 w-4" />;
      case 'shopify':
        return <Building className="h-4 w-4" />;
      case 'paypal':
        return <Users className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getPlatformColor = () => {
    switch (platformInfo.platform) {
      case 'amazon': return 'bg-orange-100 text-orange-800';
      case 'ebay': return 'bg-blue-100 text-blue-800';
      case 'etsy': return 'bg-purple-100 text-purple-800';
      case 'shopify': return 'bg-green-100 text-green-800';
      case 'paypal': return 'bg-indigo-100 text-indigo-800';
      case 'walmart': return 'bg-blue-100 text-blue-800';
      case 'target': return 'bg-red-100 text-red-800';
      case 'mercari': return 'bg-orange-100 text-orange-800';
      case 'poshmark': return 'bg-pink-100 text-pink-800';
      case 'vinted': return 'bg-green-100 text-green-800';
      case 'grailed': return 'bg-gray-100 text-gray-800';
      case 'facebook_marketplace': return 'bg-blue-100 text-blue-800';
      case 'offerup': return 'bg-teal-100 text-teal-800';
      case 'nextdoor': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPolicyInsights = () => {
    if (platformInfo.platform === 'unknown') {
      return [
        "Most platforms have buyer protection policies",
        "Check return/refund policy before purchasing",
        "Document all issues with photos/screenshots",
        "Know your chargeback rights with payment processor",
        "Consumer protection laws vary by jurisdiction"
      ];
    }
    
    // Platform-specific detailed policy insights
    const policyInsights = {
      amazon: [
        "Most items: 30-day returns, third-party sellers may differ",
        "A-to-Z Guarantee protects against non-delivery/not as described",
        "Amazon may issue direct refunds for marketplace items"
      ],
      walmart: [
        "Standard 90-day return window (in-store or online)",
        "Third-party marketplace sellers have separate policies",
        "Free return shipping labels available from account"
      ],
      target: [
        "90-day returns (120 days with RedCard)",
        "Target+ third-party items: typically 30 days",
        "Returns accepted by mail or in-store"
      ],
      bestbuy: [
        "Standard 15-day return window, extended to 30 days for Total members",
        "Select items (phones, drones) have shorter windows or restocking fees",
        "Refunds can be requested online or in-store",
        "Best Buy Marketplace orders may need to go through seller first"
      ],
      costco: [
        "Extremely generous return policies: 90 days for electronics",
        "Unlimited returns for many other categories",
        "Returns can be initiated online or at warehouse locations",
        "Membership required; refunds processed easily in most cases"
      ],
      apple: [
        "14-day return policy for most items bought directly",
        "Products must be in original condition; opened items still eligible",
        "Refunds processed via direct customer service",
        "Not through in-store reps for online orders"
      ],
      homedepot: [
        "90-day return window for most items; 180 days for cardholders",
        "Returns allowed in-store or by mail",
        "Escalations can be made to store managers or customer care",
        "Receipts or proof of purchase required"
      ],
      lowes: [
        "90-day returns on most items (matches Home Depot)",
        "Store managers typically handle disputes/escalations",
        "Receipts or proof of purchase required",
        "Refunds are flexible in most cases"
      ],
      dell: [
        "30-day return policy for most products",
        "Restocking fees may apply (especially for opened computers)",
        "Refunds handled through Dell customer service",
        "Not through resellers"
      ],
      newegg: [
        "Return policies depend on seller (if marketplace)",
        "Items often have 30-day return eligibility",
        "Restocking or return shipping fees may apply",
        "May require RMA (Return Merchandise Authorization)"
      ],
      wayfair: [
        "30-day returns for most items (except assembled/final-sale)",
        "Return shipping typically customer's responsibility",
        "Free return shipping only if item is defective",
        "Large furniture items may have special requirements"
      ],
      nike: [
        "60-day return policy for Nike.com purchases",
        "Returns accepted even for worn items",
        "Free return shipping included",
        "Refunds processed quickly via original payment method"
      ],
      adidas: [
        "30-day returns for unworn items with tags",
        "Free return shipping included",
        "Items must be in original condition",
        "Quick refund processing to original payment method"
      ],
      ebay: [
        "Money Back Guarantee covers most items",
        "Protection for damage, incorrect, or non-delivery",
        "Return periods vary: check listing (often 30 days)"
      ],
      etsy: [
        "Individual seller policies (not Etsy-wide)",
        "Etsy encourages professional issue resolution",
        "Buyer Protection for non-delivery/not as described"
      ],
      mercari: [
        "3-day window to request returns after delivery",
        "Only eligible if damaged or not as described",
        "Refunds denied after 3-day window"
      ],
      poshmark: [
        "Sales final unless damaged/not as described",
        "3-day reporting window after delivery",
        "Poshmark Support reviews all disputes"
      ],
      shopify: [
        "Policies vary by individual store",
        "Common: 14-30 day windows, check store policy",
        "No universal Shopify refund protection"
      ],
      paypal: [
        "180-day Buyer Protection window",
        "Covers non-receipt or significantly not as described",
        "Must open dispute in PayPal Resolution Center"
      ],
      vinted: [
        "2-day reporting window for issues",
        "Refunds for non-delivery, damage, or misdescription",
        "Manual dispute review by Vinted team"
      ],
      grailed: [
        "Case-by-case dispute handling",
        "PayPal Buyer Protection still applies",
        "No universal return policy from platform"
      ],
      facebook_marketplace: [
        "Purchase Protection for shipped orders only",
        "No formal policy for local pickup sales",
        "Limited refund scenarios for delivered items"
      ],
      offerup: [
        "2-day window for shipped order issues",
        "Local sales are typically final",
        "Must use OfferUp shipping for protection"
      ],
      nextdoor: [
        "No platform refund protection",
        "Direct negotiation with seller required",
        "Local community-based transactions"
      ],
      // Subscription Platforms
      apple_app_store: [
        "48-hour self-service refund window",
        "Use reportaproblem.apple.com for quick refunds",
        "Auto-renewal issues are refundable"
      ],
      google_play: [
        "48-hour automatic refund window",
        "Self-service through Play Store account",
        "App malfunction qualifies for refunds"
      ],
      netflix: [
        "No refunds for billing cycles",
        "Cancellation stops future billing only",
        "Unauthorized charges may be refunded"
      ],
      spotify: [
        "No refunds for current billing cycle",
        "Trial notifications may qualify for refunds",
        "Contact support for goodwill refunds"
      ],
      youtube_premium: [
        "Limited refunds except for billing errors",
        "Service failure may qualify for refunds",
        "Use Google support channels"
      ],
      adobe_creative_cloud: [
        "14-day refund window for new subscriptions",
        "Early termination fees may apply",
        "Annual plans have stricter policies"
      ],
      canva_pro: [
        "14-day refund for first-time upgrades",
        "No refunds on renewals unless legally required",
        "Trial cancellation issues are refundable"
      ],
      notion: [
        "72-hour refund window for annual plans",
        "No monthly plan refunds",
        "Recent charges may qualify"
      ],
      grammarly: [
        "10-day refund window for annual plans",
        "No monthly plan refunds",
        "Mistaken upgrades are refundable"
      ],
      squarespace: [
        "14-day refund for annual plans only",
        "Domain and monthly fees non-refundable",
        "Website fees separate from domain fees"
      ],
      nordvpn: [
        "30-day money-back guarantee",
        "Must not abuse usage policies",
        "All plans covered by guarantee"
      ],
      expressvpn: [
        "30-day money-back guarantee",
        "Manual cancellation required",
        "Valid transactions fully covered"
      ],
      upwork: [
        "Disputes must be filed within 14 days of milestone submission",
        "Hourly contracts protected if tracked through Upwork's time tracker",
        "Partial refunds available based on dispute resolution outcome"
      ],
      fiverr: [
        "Buyers can initiate disputes within 13 days after order delivery",
        "Resolution handled via Fiverr's Resolution Center",
        "Work must be significantly different from description for refund eligibility"
      ],
      freelancer: [
        "Disputes available for both fixed and hourly projects",
        "Arbitration possible if mutual agreement not reached",
        "Platform fees typically non-refundable unless agreed otherwise"
      ],
      peopleperhour: [
        "Refunds possible within 7 days of delivery if dispute raised",
        "Must provide evidence of work not meeting expectations",
        "Escalation available through moderation team"
      ],
      taskrabbit: [
        "Tasks can be disputed within 24 hours of completion",
        "Must submit evidence to TaskRabbit support for review",
        "TaskRabbit Happiness Pledge covers unsatisfactory work"
      ],
      // Event & Ticketing platforms
      ticketmaster: [
        "Refunds are only available if the event is canceled and not rescheduled",
        "Rescheduled events are typically non-refundable",
        "Auto-refunds processed for officially canceled events"
      ],
      stubhub: [
        "Refunds or credits issued for canceled events only",
        "FanProtect guarantee covers invalid or duplicate tickets",
        "Otherwise tickets are non-refundable unless protected"
      ],
      eventbrite: [
        "Each organizer sets their own refund policy",
        "Refunds usually not issued unless event is canceled",
        "Contact organizer directly for fastest resolution"
      ],
      seatgeek: [
        "Refunds for canceled events only",
        "Rescheduled tickets remain valid for new date",
        "SeatGeek Swaps may offer alternative solutions"
      ],
      // Accommodation platforms
      airbnb: [
        "Refunds depend on host cancellation policy (Flexible, Moderate, Strict)",
        "Full refund usually possible if canceled 24+ hours in advance under Flexible policy",
        "Safety or misrepresentation issues may qualify for refunds regardless of policy"
      ],
      booking: [
        "Refunds depend on property policy shown at booking",
        "Free cancellation must be stated at time of booking",
        "Non-refundable rates usually not eligible for refunds"
      ]
    };
    
    return policyInsights[platformInfo.platform] || [];
  };

  // If user doesn't have Pro access, show upgrade prompt
  if (!canAccessPlatformIntelligence()) {
    return (
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Crown className="h-5 w-5 text-amber-500" />
            Platform Intelligence
            <Badge className="bg-amber-100 text-amber-800">Pro Feature</Badge>
          </CardTitle>
          <CardDescription>
            Get advanced platform-specific strategies and recommendations
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Unlock detailed insights about {platformInfo.platform.charAt(0).toUpperCase() + platformInfo.platform.slice(1)}'s
            specific policies, dispute processes, and optimal refund strategies.
          </p>
          
          <Button 
            onClick={() => navigate('/pricing')}
            className="w-full gap-2"
          >
            <Crown className="h-4 w-4" />
            Upgrade to Pro
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {getPlatformIcon()}
          Platform Intelligence
        </CardTitle>
        <CardDescription>
          Automated analysis based on merchant and transaction details
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Platform Detection */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Platform:</span>
            <Badge className={getPlatformColor()}>
              {platformInfo.platform.charAt(0).toUpperCase() + platformInfo.platform.slice(1)}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Seller Type:</span>
              <Badge variant={platformInfo.isPrivateSeller ? "secondary" : "default"}>
                {platformInfo.isPrivateSeller ? "Private Seller" : "Business"}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Transaction:</span>
              <Badge variant={platformInfo.transactionType === 'C2C' ? "outline" : "default"}>
                {platformInfo.transactionType}
              </Badge>
            </div>
          </div>
        </div>

        {/* Policy Insights */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Policy Insights:
          </h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            {getPolicyInsights().slice(0, 3).map((insight, index) => (
              <li key={index} className="flex items-start gap-1">
                <span className="text-primary">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* General Recommendations */}
        {recommendations.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Key Recommendations:</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              {recommendations.slice(0, 2).map((rec, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-primary">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {platformInfo.transactionType === 'event_ticketing' && (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <p className="font-medium">Event Ticket Policy</p>
                <p className="mt-1">Event tickets are typically non-refundable unless the event is officially canceled. Check if the event was rescheduled vs canceled.</p>
              </div>
            </div>
          </div>
        )}

        {platformInfo.transactionType === 'accommodation' && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Home className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Accommodation Booking</p>
                <p className="mt-1">Refund eligibility depends on the specific cancellation policy selected at booking. Check your booking confirmation for details.</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformIntelligenceCard;
